/**
 * popoutChannel — typed BroadcastChannel wrapper for the netobs
 * "diagnostics on a second monitor" pop-out window.
 *
 * BroadcastChannel is a browser primitive that lets two same-origin
 * pages talk to each other (zero-coupling, no server roundtrip). The
 * parent (game) window publishes snapshots every ~250ms; the popout
 * (`/?popout=1`) subscribes and renders the same netobs panels with
 * the parent's data — no Colyseus connection needed in the popout.
 *
 * Supported channel: 'helix-netobs'.
 *
 * Pattern: same as Chrome DevTools / Redux DevTools / React DevTools'
 * "undock to separate window" mode. The popout has no game canvas, no
 * room state — it's a presentation-only mirror of what the parent sees.
 */

const CHANNEL_NAME = 'helix-netobs';

export interface NetobsTickFrame {
  type: 'tick';
  payload: {
    meanMs: number;
    maxMs: number;
    sampleCount: number;
    budgetMs: number;
    timestamp: number;
  };
}

export interface NetobsTrafficFrame {
  type: 'traffic';
  payload: {
    /** NetworkTracer.recentTraffic snapshot — last N events. */
    recentTraffic: Array<{
      seq: number;
      timestamp: string;
      direction: 'client->server' | 'server->client';
      type: string;
      bytes: number;
    }>;
    /** Per-message-type stats — serialized from the Map. */
    outboundStats: Array<[string, MessageTypeStatsRecord]>;
    inboundStats: Array<[string, MessageTypeStatsRecord]>;
  };
}

export interface MessageTypeStatsRecord {
  count: number;
  totalBytes: number;
  avgBytes: number;
  lastAt: string | null;
}

export interface NetobsLatencyFrame {
  type: 'latency';
  payload: {
    snaps: Array<{
      sid: string;
      name?: string;
      phase1Ms: number;
      phase2Ms: number;
      rttMs: number;
      clockReady: boolean;
      updatedAt: number;
    }>;
    timestamp: number;
  };
}

export interface NetobsHelloFrame {
  type: 'hello';
  payload: { from: 'parent' | 'popout'; sentAt: number };
}

export type NetobsFrame =
  | NetobsTickFrame
  | NetobsTrafficFrame
  | NetobsLatencyFrame
  | NetobsHelloFrame;

let cached: BroadcastChannel | null = null;

/** Lazy-create the channel. Returns null in non-browser contexts (SSR, ts-jest). */
function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return null;
  if (cached) return cached;
  try {
    cached = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
  return cached;
}

/** Publish a frame on the channel. Best-effort; silently no-ops in unsupported contexts. */
export function publishNetobsFrame(frame: NetobsFrame): void {
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage(frame);
  } catch {
    // Frame too big? Channel closed? Best-effort — drop.
  }
}

/** Subscribe to incoming frames. Returns an unsubscribe fn. */
export function subscribeNetobsFrames(handler: (frame: NetobsFrame) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (ev: MessageEvent) => {
    if (ev?.data && typeof ev.data === 'object' && 'type' in ev.data) {
      handler(ev.data as NetobsFrame);
    }
  };
  ch.addEventListener('message', listener);
  return () => {
    try { ch.removeEventListener('message', listener); } catch { /* ignore */ }
  };
}
